import { Badge, HStack, Text, VStack } from "@chakra-ui/react";
import { usePartyNames } from "../../hooks/usePartyNames";
import { furthestRequest, readParticipation } from "./placingUtils";

const shortDate = (value) => {
  const raw = value?.$date ?? value;
  return raw ? new Date(raw).toLocaleDateString(undefined, { day: "numeric", month: "short" }) : "";
};

/**
 * Where a lead has got to, as { label, colour }: the quote request's status
 * (from the comparison) when there is one, otherwise the submission's.
 */
export const marketStatus = (lead, row) => {
  if (row?.status === "nominated") return { label: "Nominated lead", colour: "green" };
  if (row?.status === "ntu") return { label: "Not taken up", colour: "orange" };
  if (row?.status === "quoted") {
    const share = readParticipation(row.quotes?.[0]).quotedShare;
    return { label: share != null ? `Quoted ${share}%` : "Quoted", colour: "purple" };
  }
  if (row?.status === "declined") return { label: "Declined to quote", colour: "red" };
  if (row?.status === "sent") return { label: row.overdue ? "Quote overdue" : "Quote requested", colour: row.overdue ? "red" : "blue" };
  if (lead?.status === "declined" || lead?.responseCode === "decline_to_participate") {
    return { label: `Declined ${shortDate(lead.respondedDate || lead.declinedDate)}`, colour: "red" };
  }
  if (lead?.responseCode === "declare_interest") return { label: `Interested ${shortDate(lead.respondedDate)}`, colour: "teal" };
  if (lead?.status === "sent") {
    return lead.viewedDate
      ? { label: `Viewed ${shortDate(lead.viewedDate)}`, colour: "teal" }
      : { label: `Sent ${shortDate(lead.sentDate)}`, colour: "green" };
  }
  return { label: "Potential lead", colour: "blue" };
};

/** The market for a submission: each lead with where it has got to. */
const MarketList = ({ submission, comparison }) => {
  const leads = submission?.sharedWithParties || [];
  const names = usePartyNames(leads.map((l) => l.partyId));
  // A carrier can have several quote requests: use its furthest-along one
  const rowsOf = (partyId) => (comparison?.rows || []).filter((r) => r.partyId === partyId);
  const rowsByParty = Object.fromEntries(leads.map((l) => [l.partyId, furthestRequest(rowsOf(l.partyId))]));

  if (leads.length === 0) {
    return <Text fontSize="sm" color="gray.500">No potential leads yet.</Text>;
  }
  return (
    <VStack align="stretch" spacing={2}>
      {leads.map((lead) => {
        const { label, colour } = marketStatus(lead, rowsByParty[lead.partyId]);
        return (
          <HStack key={lead.partyId} justify="space-between" fontSize="sm">
            <Text>{names[lead.partyId] || "Loading…"}</Text>
            <Badge colorScheme={colour} borderRadius="full" px={2} textTransform="none">{label}</Badge>
          </HStack>
        );
      })}
    </VStack>
  );
};

export default MarketList;
